import { useTransitionOrder } from '@restaurant/api-client/react';
import { nextStatuses, primaryNextStatus, type OrderDto } from '@restaurant/shared';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { toastError } from '@/lib/errors';
import { ACTION_LABEL } from './format';

/** Status buttons driven by the shared transition table; reports whether the customer was notified. */
export function OrderActions({ order, size = 'sm' }: { order: OrderDto; size?: 'sm' | 'default' }) {
  const t = useTransitionOrder();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const primary = primaryNextStatus(order.status, order.type);
  const canCancel = nextStatuses(order.status, order.type).includes('cancelled');

  const go = async (to: OrderDto['status'], why?: string) => {
    try {
      const res = await t.mutateAsync({ id: order.id, to, reason: why });
      if (res.notification && !res.notification.sent && res.notification.reason !== 'draft discarded') {
        toast.warning('Customer was not notified', { description: res.notification.reason });
      }
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {primary ? (
        <Button size={size} onClick={() => void go(primary)} disabled={t.isPending}>
          {t.isPending ? <Spinner /> : null}
          {ACTION_LABEL[primary]}
        </Button>
      ) : null}
      {canCancel ? (
        <Button size={size} variant="outline" onClick={() => (order.status === 'draft' ? void go('cancelled') : setCancelOpen(true))} disabled={t.isPending}>
          {order.status === 'draft' ? 'Discard' : 'Cancel'}
        </Button>
      ) : null}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent title={`Cancel order #${order.number}?`} description="The customer will be notified.">
          <Field label="Reason (shared in the audit log)">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. out of chicken" autoFocus />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep order</Button>
            <Button variant="destructive" onClick={() => void go('cancelled', reason || undefined).then(() => setCancelOpen(false))}>Cancel order</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
